import { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { ensure, useNotifications } from "sprint-common-web";

import { UpdateFileRecordContentRequest } from "../../../../../../common/api/master-api/file-records/update/content/UpdateFileRecordContentRequest";
import { isAncillaryContentSubtype } from "../../../../../../common/types/AncillaryContentType";
import { ContentReference, ContentVersion } from "../../../../../../common/types/ContentReference";
import { ContentReferenceType } from "../../../../../../common/types/ContentReferenceType";
import { ensureMovieContentSubtype, isMovieContentSubtype } from "../../../../../../common/types/MovieContentSubtype";
import {
    ensureTvSeriesContentSubtype,
    isTvSeriesContentSubtype
} from "../../../../../../common/types/TvSeriesContentSubtype";
import { ContentAssociationValidation } from "../../../../../common/content/types/AssociateContentFormValidationRules";
import { ContentAssociationFormState } from "../../../../../common/content/types/ContentAssociationFormState";
import {
    FileRecordAncillaryDetails,
    FileRecordContentDetails,
    FileRecordDetails,
    FileRecordMovieDetails,
    FileRecordTvSeriesDetails
} from "../../../../../common/file-record-details/FileRecordDetails";
import { getCiFileRecordQcReviewPageHref } from "../../../../../ContentIngestionRoutes";
import { useFileRecordDetails } from "../../../../common/hooks/useFileRecordDetails";
import { getAssociateFileRecordValidationRules } from "../../../common/utils/getAssociateFileRecordValidationRules";
import { validateEditContentVersions, ValidateEditContentVersionsRequest } from "../common/validateEditContentVersions";
import { useEditFileRecordContent } from "./useEditFileRecordContent";

export const useEditFileRecordContentForm = () => {
    const [defaultContentState, setDefaultContentState] = useState<ContentAssociationFormState>();

    const [isDefaultContentLoading, setIsDefaultContentLoading] = useState<boolean>(true);

    const { success, error } = useNotifications();

    const { fileRecordId } = useParams<{ fileRecordId: string }>();

    const { data: fileRecordDetails } = useFileRecordDetails(fileRecordId);
    const { updateFileRecordContent } = useEditFileRecordContent({
        invalidationKey: fileRecordDetails?.id
    });

    const history = useHistory();

    const goToFileRecordDetails = () => {
        const path = getCiFileRecordQcReviewPageHref(fileRecordId);
        history.push(path);
    };

    const onSubmit = async (contentReference?: ContentReference) => {
        const request: UpdateFileRecordContentRequest = {
            id: fileRecordId,
            content: contentReference
        };

        try {
            await updateFileRecordContent.mutateAsync(request);

            goToFileRecordDetails();

            success("File record content successfully updated");
        } catch (e) {
            error("Something went wrong while updating file record content");
        }
    };

    const validateMovieAndTvVersions = (versions: ContentVersion[], versionIds?: string[]) => {
        ensure(fileRecordDetails);

        const isOrphanedChangeAllowed = getIsChangeForOrphanedAllowed(fileRecordDetails);
        const isVersionRequired = getIsVersionRequired(fileRecordDetails);

        const request: ValidateEditContentVersionsRequest = {
            selectedVersionIds: versionIds ?? [],
            versions,
            isOrphanedChangeAllowed,
            isVersionRequired
        };

        return validateEditContentVersions(request);
    };

    const validation: ContentAssociationValidation = {
        rules: getAssociateFileRecordValidationRules(),
        actions: { validateVersions: validateMovieAndTvVersions }
    };

    return {
        defaultContentState,
        setDefaultContentState,
        isDefaultContentLoading,
        setIsDefaultContentLoading,
        goToFileRecordDetails,
        onSubmit,
        validation,
        fileRecordDetails
    };
};

export const mapToContentAssociationFormState = (fileRecordDetails: FileRecordDetails): ContentAssociationFormState => {
    const { content } = fileRecordDetails;

    if (!content) {
        return {};
    }

    if (isFileRecordDetailsMovieContent(content)) {
        return {
            contentType: ContentReferenceType.Movie,
            contentId: content.movie.id,
            versionIds: content.versions.map((version) => version.id)
        };
    }

    if (isFileRecordDetailsTvSeriesContent(content)) {
        return {
            contentType: ContentReferenceType.TvSeries,
            contentId: content.tvSeries?.id,
            episodeId: content.episode?.id,
            seasonId: content.season?.id,
            versionIds: content.versions.map((version) => version.id)
        };
    }

    if (isFileRecordDetailsAncillaryContent(content)) {
        return {
            contentType: ContentReferenceType.Ancillary,
            contentId: content.ancillary.id,
            airlineId: content.airline.id
        };
    }

    throw new Error("Unsupported content subtype");
};

const getIsVersionRequired = (fileRecord: FileRecordDetails): boolean => {
    const { content, isNotificationApproved } = fileRecord;

    if (!content) {
        return false;
    }

    if (isTvSeriesContentSubtype(content.contentSubtype)) {
        ensureTvSeriesContentSubtype(content.contentSubtype);
        return isNotificationApproved && !!content.versions.length;
    }

    if (isMovieContentSubtype(content.contentSubtype)) {
        ensureMovieContentSubtype(content.contentSubtype);
        return isNotificationApproved && !!content.versions.length;
    }

    return false;
};

const getIsChangeForOrphanedAllowed = (fileRecord: FileRecordDetails): boolean => {
    const { content, isNotificationApproved } = fileRecord;

    if (!content || !isNotificationApproved) {
        return true;
    }

    if (isTvSeriesContentSubtype(content.contentSubtype)) {
        ensureTvSeriesContentSubtype(content.contentSubtype);
        return !content.versions.every((v) => v.buzzId);
    }

    if (isMovieContentSubtype(content.contentSubtype)) {
        ensureMovieContentSubtype(content.contentSubtype);
        return !content.versions.every((v) => v.buzzId);
    }

    return false;
};

const isFileRecordDetailsMovieContent = (content: FileRecordContentDetails): content is FileRecordMovieDetails => {
    return isMovieContentSubtype(content.contentSubtype);
};

const isFileRecordDetailsTvSeriesContent = (
    content: FileRecordContentDetails
): content is FileRecordTvSeriesDetails => {
    return isTvSeriesContentSubtype(content.contentSubtype);
};

const isFileRecordDetailsAncillaryContent = (
    content: FileRecordContentDetails
): content is FileRecordAncillaryDetails => {
    return isAncillaryContentSubtype(content.contentSubtype);
};
